import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Hash, Type, Medal, User, Grid3x3 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import Header from "../components/Header";
import RatingPanel from "../components/RatingPanel";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { localStartOfWeek, localStartOfLastWeek, localStartOfMonth } from "../lib/timeWindow";
import { toast } from "sonner";

const WINDOW_OPTIONS = [
  { value: "week",      label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "month",     label: "This month" },
  { value: "all",       label: "All time" },
];

// Only used so a board is always selectable before anyone has played it —
// the real options come from what has actually been played.
const SEED_COUNTS = {
  numbers: [20, 40, 60, 100],
  words: [10, 20, 30, 50],
  names: [5, 10, 15, 20],
  working_memory: [60, 120, 180, 300],
};

const WORD_DIFFICULTIES = [
  { value: "simple", label: "Simple" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
  { value: "complex", label: "Complex" },
];

const WM_SPEEDS = [
  { value: "speed_500ms", label: "0.5s · Fast" },
  { value: "speed_1000ms", label: "1.0s · Standard" },
  { value: "speed_2000ms", label: "2.0s · Slower" },
];

const countLabel = (mode, n) => {
  if (mode === "numbers") return `${n} digits`;
  if (mode === "names") return `${n} faces`;
  if (mode === "working_memory") return `${n}s`;
  return `${n} words`;
};

const playersLabel = (n) => {
  if (!n) return "no players yet";
  return n === 1 ? "1 player" : `${n} players`;
};

// Two distinct dates: when the displayed score was set, and when the player
// actually last trained this game. They are not the same thing — the old row
// showed the score's date labelled "Last played".
const EntryDates = ({ entry, index, formatShortDate }) => {
  const best = formatShortDate(entry.best_at);
  const last = formatShortDate(entry.last_played_at) || best;
  if (!best && !last) return null;
  return (
    <div
      className="uppercase tracking-wider text-[10px] sm:text-xs text-muted-foreground/70"
      data-testid={`entry-dates-${index}`}
    >
      {best && (
        <span data-testid={`entry-best-at-${index}`}>Best · {best}</span>
      )}
      {best && last && <span className="mx-1.5 text-muted-foreground/40">·</span>}
      {last && (
        <span data-testid={`entry-last-played-${index}`}>Last played · {last}</span>
      )}
    </div>
  );
};

const Leaderboard = ({ user }) => {  const navigate = useNavigate();
  const location = useLocation();
  // Allow deep-linking via ?mode=working_memory (or any mode)
  const initialMode = (() => {
    const p = new URLSearchParams(location.search).get("mode");
    return ["numbers", "words", "names", "working_memory", "overall"].includes(p) ? p : "numbers";
  })();
  const [mode, setMode] = useState(initialMode);
  const [difficulty, setDifficulty] = useState("simple");
  const [wmSpeed, setWmSpeed] = useState("speed_1000ms");
  const [itemsCount, setItemsCount] = useState(initialMode === "working_memory" ? "120" : "40");
  const [timeWindow, setTimeWindow] = useState("week");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boards, setBoards] = useState([]);

  const currentDifficulty =
    mode === "words" ? difficulty : mode === "working_memory" ? wmSpeed : null;

  const windowParams = () => {
    if (timeWindow === "week") return `&start_iso=${encodeURIComponent(localStartOfWeek())}`;
    if (timeWindow === "last_week")
      return `&start_iso=${encodeURIComponent(localStartOfLastWeek())}&end_iso=${encodeURIComponent(localStartOfWeek())}`;
    if (timeWindow === "month") return `&start_iso=${encodeURIComponent(localStartOfMonth())}`;
    return "";
  };

  // Build the filter options from boards that have actually been played, and
  // land the user on a populated board rather than an empty one.
  useEffect(() => {
    if (mode === "overall") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API}/leaderboard/options?mode=${mode}&window=${timeWindow}${windowParams()}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setBoards(data.boards || []);
        const played = data.boards || [];
        const matches = played.some(
          (b) =>
            String(b.items_count) === String(itemsCount) &&
            (currentDifficulty ? b.difficulty === currentDifficulty : true)
        );
        if (!matches && data.default) {
          setItemsCount(String(data.default.items_count));
          if (mode === "words" && data.default.difficulty) setDifficulty(data.default.difficulty);
          if (mode === "working_memory" && data.default.difficulty) setWmSpeed(data.default.difficulty);
        }
      } catch {
        /* options are a convenience — the board still loads */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, timeWindow]);

  const countOptions = useMemo(() => {
    const counts = new Set(SEED_COUNTS[mode] || []);
    boards.forEach((b) => counts.add(b.items_count));
    return [...counts]
      .sort((a, b) => a - b)
      .map((n) => ({
        value: String(n),
        label: countLabel(mode, n),
        players: boards
          .filter(
            (b) =>
              b.items_count === n &&
              (currentDifficulty ? b.difficulty === currentDifficulty : true)
          )
          .reduce((sum, b) => sum + b.player_count, 0),
      }));
  }, [boards, mode, currentDifficulty]);

  const difficultyOptions = useMemo(() => {
    const base = mode === "working_memory" ? WM_SPEEDS : WORD_DIFFICULTIES;
    return base.map((d) => ({
      ...d,
      players: boards
        .filter((b) => b.difficulty === d.value && String(b.items_count) === String(itemsCount))
        .reduce((sum, b) => sum + b.player_count, 0),
    }));
  }, [boards, mode, itemsCount]);

  useEffect(() => {
    fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, difficulty, itemsCount, wmSpeed, timeWindow]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      let url;
      if (mode === "overall") {
        url = `${API}/leaderboard/overall?window=${timeWindow}${windowParams()}`;
      } else {
        url = `${API}/leaderboard?mode=${mode}&items_count=${itemsCount}&window=${timeWindow}${windowParams()}`;
        if (currentDifficulty) url += `&difficulty=${currentDifficulty}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return "—";
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Short local date like "Tue 22 Jul" — for the "Last played" tag on cards.
  const formatShortDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    } catch {
      return null;
    }
  };

  const getMedalColor = (rank) => {
    switch (rank) {
      case 0: return "text-yellow-400";
      case 1: return "text-gray-300";
      case 2: return "text-amber-600";
      default: return "text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="leaderboard-page">
      <Header user={user} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Mode Tabs + the top of the scale, stated once */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8">
            <Tabs value={mode} onValueChange={setMode} className="flex-1">
              <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 bg-muted h-auto">
              <TabsTrigger value="overall" data-testid="tab-overall">
                <Trophy className="w-4 h-4 mr-2" />
                Overall
              </TabsTrigger>
              <TabsTrigger value="numbers" data-testid="tab-numbers">
                <Hash className="w-4 h-4 mr-2" />
                Numbers
              </TabsTrigger>
              <TabsTrigger value="words" data-testid="tab-words">
                <Type className="w-4 h-4 mr-2" />
                Words
              </TabsTrigger>
              <TabsTrigger value="names" data-testid="tab-names">
                <User className="w-4 h-4 mr-2" />
                Names
              </TabsTrigger>
              <TabsTrigger value="working_memory" data-testid="tab-working-memory">
                <Grid3x3 className="w-4 h-4 mr-2" />
                Working Memory
              </TabsTrigger>
            </TabsList>
            </Tabs>
            <RatingPanel
              mode={mode === "overall" ? "words" : mode}
              itemsCount={mode === "overall" ? 30 : Number(itemsCount)}
              difficulty={mode === "words" ? difficulty : undefined}
              trigger={
                <button
                  type="button"
                  data-testid="scale-chip"
                  className="shrink-0 self-start sm:self-auto px-3 py-2 rounded-full border border-primary/40 bg-primary/10 text-primary font-mono text-[10px] sm:text-xs uppercase tracking-widest hover:border-primary hover:bg-primary/20 transition-colors"
                >
                  {mode === "overall" ? "Points · no ceiling" : "Max rating 150"}
                </button>
              }
            />
          </div>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            {/* Time window pills */}
            <div className="flex-1 min-w-full flex flex-wrap gap-2" data-testid="window-pills">
              {WINDOW_OPTIONS.map((w) => {
                const active = timeWindow === w.value;
                return (
                  <button
                    key={w.value}
                    data-testid={`window-${w.value}`}
                    onClick={() => setTimeWindow(w.value)}
                    className={`px-4 py-2 rounded-full text-sm font-mono uppercase tracking-widest transition-all border-2 ${
                      active
                        ? "border-primary bg-primary/20 text-primary"
                        : "border-border bg-card/40 text-muted-foreground hover:border-primary/40"
                    }`}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className={`flex flex-wrap gap-4 mb-8 ${mode === "overall" ? "hidden" : ""}`}>
            <div className="flex-1 min-w-[150px]">
              <label className="text-sm text-muted-foreground mb-2 block">
                {mode === "numbers" ? "Digit Count" : mode === "names" ? "Face Count" : mode === "working_memory" ? "Session Duration" : "Word Count"}
              </label>
              <Select value={itemsCount} onValueChange={setItemsCount}>
                <SelectTrigger data-testid="items-count-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`items-count-option-${o.value}`}>
                      {o.label}
                      <span className="text-muted-foreground"> · {playersLabel(o.players)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(mode === "words" || mode === "working_memory") && (
              <div className="flex-1 min-w-[150px]">
                <label className="text-sm text-muted-foreground mb-2 block">
                  {mode === "words" ? "Difficulty" : "Item Speed"}
                </label>
                <Select
                  value={currentDifficulty}
                  onValueChange={mode === "words" ? setDifficulty : setWmSpeed}
                >
                  <SelectTrigger data-testid={mode === "words" ? "difficulty-select" : "wm-speed-select"}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {difficultyOptions.map((d) => (
                      <SelectItem key={d.value} value={d.value} data-testid={`difficulty-option-${d.value}`}>
                        {d.label}
                        <span className="text-muted-foreground"> · {playersLabel(d.players)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Leaderboard */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                {mode === "overall"
                  ? "Overall — every game combined"
                  : `Top Performers - ${mode === "numbers" ? `${itemsCount} Digits` : mode === "names" ? `${itemsCount} Faces` : mode === "working_memory" ? `${itemsCount}s · ${wmSpeed.replace("speed_","").replace("ms"," ms")}` : `${itemsCount} ${difficulty.charAt(0).toUpperCase() + difficulty.slice(1)} Words`}`}
              </CardTitle>
              {mode === "overall" && (
                <p className="text-sm text-muted-foreground">
                  Points, not ratings — your best session in each game added together, so
                  bigger rounds count for more. Untrained games count zero.
                </p>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-muted animate-pulse">
                      <div className="w-8 h-8 rounded-full bg-background"></div>
                      <div className="flex-1 h-4 bg-background rounded"></div>
                      <div className="w-16 h-4 bg-background rounded"></div>
                    </div>
                  ))}
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-12">
                  <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No Entries Yet</h3>
                  <p className="text-muted-foreground mb-6">
                    Be the first to top this leaderboard!
                  </p>
                  <Button
                    onClick={() => navigate(mode === "numbers" ? "/train/numbers" : mode === "names" ? "/train/names" : mode === "working_memory" ? "/train/working-memory" : mode === "overall" ? "/dashboard" : "/train/words")}
                    className={
                      mode === "numbers" ? "bg-primary text-primary-foreground" : 
                      mode === "names" ? "bg-orange-500 text-white" : 
                      mode === "working_memory" ? "bg-purple-500 text-white" :
                      "bg-secondary text-secondary-foreground"
                    }
                  >
                    Start Training
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {entries.map((entry, index) => {
                    const isCurrentUser = entry.user_id === user?.user_id;
                    return (
                      <motion.div
                        key={entry.entry_id || index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 border ${
                          isCurrentUser 
                            ? "border-primary/50 bg-primary/5" 
                            : "border-border bg-muted/50"
                        }`}
                        data-testid={`leaderboard-entry-${index}`}
                      >
                        {/* Rank */}
                        <div className="w-6 sm:w-10 text-center shrink-0">
                          {index < 3 ? (
                            <Medal className={`w-5 h-5 sm:w-6 sm:h-6 mx-auto ${getMedalColor(index)}`} />
                          ) : (
                            <span className="text-base sm:text-lg font-mono text-muted-foreground">
                              {index + 1}
                            </span>
                          )}
                        </div>

                        {/* User */}
                        <Avatar className="w-8 h-8 sm:w-10 sm:h-10 shrink-0">
                          <AvatarImage src={entry.user_picture} />
                          <AvatarFallback className={
                          mode === "numbers" ? "bg-primary/10 text-primary" : 
                          mode === "names" ? "bg-orange-500/10 text-orange-500" : 
                          "bg-secondary/10 text-secondary"
                        }>
                            {entry.user_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {entry.user_name}
                            {isCurrentUser && (
                              <span className="text-xs text-primary ml-2">(You)</span>
                            )}
                          </div>
                          {mode === "overall" ? (
                            <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs sm:text-sm text-muted-foreground font-mono" data-testid={`entry-parts-${index}`}>
                              {["numbers", "words", "names", "working_memory"].map((m) => (
                                <span key={m}>
                                  <span className="text-muted-foreground/70 uppercase">
                                    {m === "working_memory" ? "Grid" : m.slice(0, 3)}
                                  </span>{" "}
                                  {entry.parts?.[m] != null ? entry.parts[m] : "—"}
                                </span>
                              ))}
                            </div>
                          ) : mode === "working_memory" ? (
                            <div className="mt-0.5 space-y-0.5 text-xs sm:text-sm text-muted-foreground font-mono">
                              <div data-testid={`entry-time-${index}`}>
                                Level {Math.round((entry.best_score || 0) / 10)} of 10
                              </div>
                              <EntryDates entry={entry} index={index} formatShortDate={formatShortDate} />
                            </div>
                          ) : (
                            <div className="mt-0.5 space-y-0.5 text-xs sm:text-sm text-muted-foreground font-mono">
                              <div data-testid={`entry-recalled-${index}`}>
                                <span className="text-green-500/90">Recalled</span>{" "}
                                {entry.best_correct_credit != null
                                  ? `${Math.round(entry.best_correct_credit * 10) / 10} / ${entry.items_count}`
                                  : `— / ${entry.items_count}`}
                              </div>
                              <div data-testid={`entry-times-${index}`}>
                                <span className="text-lime-400/80">Mem</span>{" "}
                                {formatTime(entry.best_memorize_seconds)}
                                <span className="mx-1.5 text-muted-foreground/40">·</span>
                                <span className="text-blue-400/80">Recall</span>{" "}
                                {formatTime(entry.best_time)}
                                {entry.best_speed_adjusted === false && (
                                  <span className="ml-1.5 text-amber-400/70" data-testid={`entry-no-speed-${index}`}>
                                    · no speed adj.
                                  </span>
                                )}
                              </div>
                              <EntryDates entry={entry} index={index} formatShortDate={formatShortDate} />
                            </div>
                          )}
                        </div>

                        {/* Rating / points */}
                        <div className="text-right shrink-0">
                          <div className={`text-2xl font-bold ${
                            mode === "numbers" ? "text-primary" :
                            mode === "names" ? "text-orange-500" :
                            mode === "working_memory" ? "text-purple-400" :
                            mode === "overall" ? "text-yellow-400" :
                            "text-secondary"
                          }`} data-testid={`entry-score-${index}`}>
                            {mode === "overall"
                              ? entry.total_points
                              : Math.round(entry.best_score)}
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70">
                            {mode === "overall" ? "points" : "rating"}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default Leaderboard;
